import { createContext, ReactNode, useContext, useState } from 'react'
import { bridge } from '~/services/bridge'

export type SidebarTabType = 'chats' | 'tasks'

// Type for the sidebar data that will be shared
export interface SidebarData {
  collapsed: boolean
  setCollapsed: (value: boolean) => void
  toggleCollapsed: () => void
  isSearching: boolean
  setIsSearching: (isSearching: boolean) => void
  activeTab: SidebarTabType
  setActiveTab: (tab: SidebarTabType) => void
}

// Create the context
const SidebarContext = createContext<SidebarData | null>(null)

// Provider component
interface SidebarProviderProps {
  children: ReactNode
}

export function SidebarProvider({ children }: SidebarProviderProps) {
  const [collapsed, setCollapsed] = useState<boolean>(
    bridge.isInPlugin() ? true : localStorage.getItem('curie-sidebar-collapsed') === 'true'
  )

  const toggleCollapsed = () => {
    localStorage.setItem('curie-sidebar-collapsed', (!collapsed).toString())
    setCollapsed(!collapsed)
  }

  const setCollapsedValue = (value: boolean) => {
    localStorage.setItem('curie-sidebar-collapsed', value.toString())
    setCollapsed(value)
  }

  const [isSearching, setIsSearching] = useState<boolean>(false)

  const [activeTab, setActiveTabState] = useState<SidebarTabType>(
    (localStorage.getItem('curie-sidebar-view') as SidebarTabType) || 'chats'
  )

  const setActiveTab = (tab: SidebarTabType) => {
    localStorage.setItem('curie-sidebar-view', tab)
    setActiveTabState(tab)
  }

  return (
    <SidebarContext.Provider
      value={{
        collapsed,
        setCollapsed: setCollapsedValue,
        toggleCollapsed,
        isSearching,
        setIsSearching,
        activeTab,
        setActiveTab
      }}
    >
      {children}
    </SidebarContext.Provider>
  )
}

const noopBool = () => {}
const noopTab = () => {}
const defaultSidebarData: SidebarData = {
  collapsed: false,
  setCollapsed: noopBool,
  toggleCollapsed: () => {},
  isSearching: false,
  setIsSearching: noopBool,
  activeTab: 'chats',
  setActiveTab: noopTab
}

// Hook to use the context. Returns safe defaults when no provider is present
// (e.g. super admin panel task modal) so callers don't need to wrap in SidebarProvider.
export function useSidebarContext(): SidebarData {
  const context = useContext(SidebarContext)
  return context ?? defaultSidebarData
}
